import { Store, Package, ShoppingCart, AlertCircle, CheckCircle, XCircle, DollarSign, Users } from "lucide-react";
import { useState, useEffect, useMemo, useCallback, memo } from "react";
import { useNavigate } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { StatsLoadingSkeleton } from "@/components/LoadingSkeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useWebshopFilter } from "@/contexts/WebshopFilterContext";
import { useToast } from "@/hooks/use-toast";
import { useDashboardStats } from "@/hooks/use-dashboard-stats";
import { supabase } from "@/integrations/supabase/client";

// Memoized StatsCard for better performance
const StatsCard = memo(({ stat }: { stat: any }) => (
  <Card className="hover:shadow-lg transition-shadow">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
      <stat.icon className="h-4 w-4 text-muted-foreground" />
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{stat.value}</div>
      <p className="text-xs text-muted-foreground">{stat.description}</p>
    </CardContent>
  </Card>
));
StatsCard.displayName = 'StatsCard';

export default function Dashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { availableWebshops, loading: webshopsLoading } = useWebshopFilter();
  const { isCustomer } = useAuth();
  const [lowStockProducts, setLowStockProducts] = useState<Array<{ name: string; stock: number; status: 'empty' | 'critical' | 'low' }>>([]);
  const [lastSyncDate, setLastSyncDate] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<'success' | 'error' | 'pending'>('success');
  const [loading, setLoading] = useState(true);
  const [requestSending, setRequestSending] = useState(false);

  const webshopIds = useMemo(() => 
    availableWebshops?.map((w: any) => w.id) || [], 
    [availableWebshops]
  );
  
  const { stats, loading: statsLoading } = useDashboardStats(
    webshopsLoading ? [] : webshopIds
  );

  const fetchStats = useCallback(async () => {
    if (!webshopIds || webshopIds.length === 0) {
      setLoading(false);
      return;
    }

    try {
      const { data: products, error: productsError } = await supabase
        .from("articles")
        .select("name, stock_count, visible_on_web");

      if (productsError) throw productsError;

      const lowStock = products?.filter(p => p.visible_on_web && (p.stock_count ?? 0) < 10)
        .sort((a, b) => (a.stock_count ?? 0) - (b.stock_count ?? 0))
        .slice(0, 3)
        .map(p => {
          const stock = p.stock_count ?? 0;
          const status: 'empty' | 'critical' | 'low' = stock === 0 ? 'empty' : stock < 3 ? 'critical' : 'low';
          return {
            name: p.name,
            stock,
            status
          };
        }) || [];

      // Check last sync date
      const { data: lastArticle } = await supabase
        .from('articles')
        .select('updated_at')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (lastArticle) {
        setLastSyncDate(lastArticle.updated_at);
        setSyncStatus('success');
      }

      setLowStockProducts(lowStock);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Kunne ikke hente statistikk";
      toast({
        title: "Feil",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, webshopIds]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleRequestWebshop = useCallback(async () => {
    try {
      setRequestSending(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Feil",
          description: "Du må være logget inn for å sende en forespørsel",
          variant: "destructive",
        });
        return;
      }

      // Check if user already has a pending request
      const { data: existingRequest } = await supabase
        .from('webshop_requests')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .maybeSingle();

      if (existingRequest) {
        toast({
          title: "Forespørsel allerede sendt",
          description: "Du har allerede en ventende forespørsel. Vi vil kontakte deg snart.",
          variant: "default",
        });
        return;
      }

      const { error } = await supabase
        .from('webshop_requests')
        .insert({
          user_id: user.id,
          message: 'Kunde ønsker å opprette en nettbutikk',
        });

      if (error) throw error;

      toast({
        title: "Forespørsel sendt!",
        description: "Vi har mottatt din forespørsel og vil kontakte deg snart.",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Kunne ikke sende forespørsel";
      toast({
        title: "Feil",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setRequestSending(false);
    }
  }, [toast]);

  // Memoized dashboard stats
  const dashboardStats = useMemo(() => [
    {
      title: "Totale Ordrer",
      value: stats.totalOrders,
      description: "I alle nettbutikker",
      icon: ShoppingCart,
    },
    {
      title: "Total Omsetning",
      value: `${stats.totalRevenue.toLocaleString('nb-NO', { style: 'currency', currency: 'NOK' })}`,
      description: "Lifetime verdi",
      icon: DollarSign,
    },
    {
      title: "Aktive Produkter",
      value: stats.totalProducts,
      description: "Totalt antall produkter",
      icon: Package,
    },
    {
      title: "Registrerte Kunder",
      value: stats.totalCustomers,
      description: "Totalt antall kunder",
      icon: Users,
    },
  ], [stats]);

  if (loading || webshopsLoading || statsLoading) {
    return <StatsLoadingSkeleton />;
  }

  // Show "no webshop" message only for customers who have no accessible webshops
  if (availableWebshops.length === 0 && isCustomer) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-muted flex items-center justify-center">
              <Store className="h-6 w-6 text-muted-foreground" />
            </div>
            <CardTitle>Ingen nettbutikk tilgjengelig</CardTitle>
            <CardDescription className="mt-2">
              Du har for øyeblikket ikke tilgang til noen nettbutikker. 
              Send en forespørsel for å komme i gang.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            <Button 
              onClick={handleRequestWebshop}
              loading={requestSending}
              loadingText="Sender forespørsel..."
              size="lg"
              className="w-full"
              aria-label="Be om opprettelse av ny nettbutikk"
            >
              Be om opprettelse av nettbutikk
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              En partner vil kontakte deg så snart som mulig for å hjelpe deg med å opprette en nettbutikk.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Oversikt over dine PCKasse webshop integrasjoner
        </p>
      </div>

      {/* Stats Cards - Mobile: 2 cols, Desktop: 4 cols */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {dashboardStats.map((stat) => (
          <StatsCard key={stat.title} stat={stat} />
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Low Stock Alert - Full width on mobile */}
        <Card className="md:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Lav lagerstatus</CardTitle>
              <AlertCircle className="h-4 w-4 text-destructive" aria-label="Advarsel om lav lagerstatus" />
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {lowStockProducts.length > 0 ? (
              lowStockProducts.map((product, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{product.name}</span>
                    <Badge
                      variant={
                        product.status === "empty"
                          ? "destructive"
                          : product.status === "critical"
                          ? "destructive"
                          : "secondary"
                      }
                      aria-label={`${product.stock} stykker på lager - ${product.status === "empty" ? "tomt" : product.status === "critical" ? "kritisk lavt" : "lavt"}`}
                    >
                      {product.stock} stk
                    </Badge>
                  </div>
                  <Progress
                    value={(product.stock / 10) * 100}
                    className="h-2"
                    aria-label={`Lagerstatus for ${product.name}: ${product.stock} av 10`}
                  />
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">Alle produkter har tilstrekkelig lager</p>
            )}
          </CardContent>
        </Card>

        {/* Popular Products */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Nye ordrer</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats.pendingOrders > 0 ? (
              <div className="flex items-center justify-between">
                <span className="text-sm">Ventende ordrer</span>
                <Badge>{stats.pendingOrders}</Badge>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Ingen nye ordrer</p>
            )}
            <button
              onClick={() => navigate("/orders")}
              className="text-sm text-primary hover:underline"
            >
              Gå til ordrer →
            </button>
          </CardContent>
        </Card>

        {/* Sync Status - Full width on mobile */}
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Synkroniseringsstatus</CardTitle>
            <CardDescription>Status for synkronisering med PCKasse</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {syncStatus === 'success' ? (
                  <CheckCircle className="h-5 w-5 text-green-500" aria-label="Synkronisering vellykket" />
                ) : syncStatus === 'error' ? (
                  <XCircle className="h-5 w-5 text-red-500" aria-label="Synkroniseringsfeil" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-yellow-500" aria-label="Venter på synkronisering" />
                )}
                <span className="font-medium">
                  {syncStatus === 'success' ? 'Synkronisert' : syncStatus === 'error' ? 'Feil' : 'Venter'}
                </span>
              </div>
              <Badge 
                variant={syncStatus === 'success' ? 'default' : syncStatus === 'error' ? 'destructive' : 'secondary'}
                aria-label={`Status: ${syncStatus === 'success' ? 'Oppdatert' : syncStatus === 'error' ? 'Feilet' : 'Venter'}`}
              >
                {syncStatus === 'success' ? 'Oppdatert' : syncStatus === 'error' ? 'Feilet' : 'Venter'}
              </Badge>
            </div>
            {lastSyncDate && (
              <p className="text-sm text-muted-foreground" aria-live="polite">
                Sist synkronisert: {new Date(lastSyncDate).toLocaleString('nb-NO')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
